import { useCallback } from 'react';
import { useAuthContext } from 'contexts/AuthContext';
import type { AuthUser } from 'types/auth';
import { AuthRole, AppRole } from 'types/auth';

export interface UseAuthResult {
  /** Whether authentication is in progress */
  isLoading: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Authenticated user info */
  user: AuthUser | null;
  /** Authentication error */
  error: string | null;
  /** Sign in */
  login: () => Promise<void>;
  /** Sign out */
  logout: () => Promise<void>;
  /** Get access token for API calls */
  getAccessToken: () => Promise<string | null>;
  /** Check if user has a specific role */
  hasRole: (role: AuthRole) => boolean;
  /** Check if user has any of the specified roles */
  hasAnyRole: (roles: AuthRole[]) => boolean;
  /** Check if user is a supervisor */
  isSupervisor: boolean;
  /** Check if user is an analyst */
  isAnalyst: boolean;
  /** Check if user has a specific app role (based on security groups) */
  hasAppRole: (role: AppRole) => boolean;
  /** Check if user is a SOC operator (member of soc-operators-asg) */
  isOperator: boolean;
  /** Check if user is a SOC admin (member of soc-admin-asg) */
  isAdmin: boolean;
  /** Whether user has access to the app (member of required security groups) */
  hasAppAccess: boolean;
  /** Whether user can take ownership of incidents */
  canTakeOwnership: boolean;
  /** Whether user can assign ownership to other users */
  canAssignOwnership: boolean;
}

/**
 * Hook for accessing authentication state and operations.
 * Provides role-checking utilities for authorization.
 */
export function useAuth(): UseAuthResult {
  const {
    isLoading,
    isAuthenticated,
    user,
    error,
    login,
    logout,
    getAccessToken,
    hasAppAccess,
  } = useAuthContext();

  const hasRole = useCallback(
    (role: AuthRole): boolean => user?.roles.includes(role) ?? false,
    [user],
  );

  const hasAnyRole = useCallback(
    (roles: AuthRole[]): boolean => roles.some((role) => user?.roles.includes(role) ?? false),
    [user],
  );

  const hasAppRole = useCallback(
    (role: AppRole): boolean => user?.appRoles.includes(role) ?? false,
    [user],
  );

  const isSupervisor = hasRole(AuthRole.Supervisor);
  const isAnalyst = hasRole(AuthRole.Analyst);
  const isOperator = hasAppRole(AppRole.Operator);
  const isAdmin = hasAppRole(AppRole.Admin);

  return {
    isLoading,
    isAuthenticated,
    user,
    error,
    login,
    logout,
    getAccessToken,
    hasRole,
    hasAnyRole,
    isSupervisor,
    isAnalyst,
    hasAppRole,
    isOperator,
    isAdmin,
    hasAppAccess,
    // Operators and Admins can take ownership; only Admins can assign to others.
    canTakeOwnership: isOperator || isAdmin,
    canAssignOwnership: isAdmin,
  };
}
